import { notification } from 'antd'
import axios from 'axios'
import Cookies from 'js-cookie'
import { clearStoredSession, getLoggedInUserInfo } from 'utils'
import NProgress from 'nprogress'
import { history, store } from 'index'
import { toggleLoading } from 'redux/settings/actions'
import { updateCopilotToken } from 'services/copilotWidget'
import {
  freezeSession,
  isSessionFrozen,
  isSessionTeardownInProgress,
  endSessionTeardown,
  getSessionInvalidationCode,
  isSessionInvalidationCode,
  SESSION_INVALIDATED_CODE,
} from 'utils/sessionFreeze'
import qs from 'qs'
import { hideExecutiveWorkforceRecords } from 'utils/workforceVisibility'

const sanitizeParams = (params = {}) =>
  Object.keys(params).reduce((acc, key) => {
    const value = params[key]

    if (value === undefined || value === null || value === 'undefined') {
      return acc
    }

    acc[key] = value
    return acc
  }, {})

const isPlainObject = (value) => Object.prototype.toString.call(value) === '[object Object]'

// This is changed
const getErrorDescription = (desc) => {
  if (!desc) return undefined
  if (typeof desc === 'object') {
    return desc.message || desc.error || JSON.stringify(desc)
  }
  return String(desc)
}

const transformEmpCodePayload = (value, toWireFormat = false) => {
  if (Array.isArray(value)) {
    return value.map((item) => transformEmpCodePayload(item, toWireFormat))
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.entries(value).reduce((acc, [key, nestedValue]) => {
    const transformedValue = transformEmpCodePayload(nestedValue, toWireFormat)

    if (toWireFormat && key === 'empCode') {
      // Backward-compatible wire payload: support legacy empId/empID and new emp_code together.
      acc.emp_code = transformedValue
      acc.empId = transformedValue
      acc.empID = transformedValue
      return acc
    }

    if (!toWireFormat && (key === 'emp_code' || key === 'empId')) {
      if (!Object.prototype.hasOwnProperty.call(acc, 'empCode')) {
        acc.empCode = transformedValue
      }
      return acc
    }

    acc[key] = transformedValue
    return acc
  }, {})
}

const transformEmpCodeFormData = (value, toWireFormat = false) => {
  const transformed = new FormData()

  value.forEach((entryValue, entryKey) => {
    if (toWireFormat && entryKey === 'empCode') {
      if (entryValue === undefined || entryValue === null || entryValue === 'undefined') {
        return
      }
      transformed.append('emp_code', entryValue)
      transformed.append('empId', entryValue)
      transformed.append('empID', entryValue)
      return
    }

    transformed.append(entryKey, entryValue)
  })

  return transformed
}

const apiClient = axios.create({
  baseURL:
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEV_BASE_URL
      : process.env.REACT_APP_PROD_BASE_URL,

  paramsSerializer: (params) => qs.stringify(sanitizeParams(params), { arrayFormat: 'brackets' }),
})

const refreshClient = axios.create({
  baseURL:
    process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_DEV_BASE_URL
      : process.env.REACT_APP_PROD_BASE_URL,
})

let refreshTokenRequest = null
let authRedirectInProgress = false

// Anything under auth/ is part of signing back in, so it stays allowed while a frozen
// session is being torn down (login, OTP, forgot/reset password).
const PUBLIC_AUTH_ROUTE = /^auth\//
const LOGIN_ROUTE = /^auth\/(login|verify-token|verify-google-token|send-otp|verify-otp)(?:[/?]|$)/

const stopRequestLoader = () => {
  store.dispatch(toggleLoading({ isLoading: false }))
  NProgress.done()
}

const clearSessionAndRedirect = (reasonCode) => {
  // Only the backend's explicit SESSION_INVALIDATED code raises the freeze flow. Every
  // other 401/403 -- an expired token, a failed refresh, a server-side session version
  // that simply is not there -- is an ordinary expiry and gets the plain sign-out.
  if (isSessionInvalidationCode(reasonCode)) {
    // admin-triggered config change: freeze the screen instead of an immediate logout
    freezeSession(
      "Your organization's configuration was updated by an administrator. Please refresh the page to continue.",
    )
    return
  }

  // Tears down every session store, including the subscribed-feature list in
  // localStorage, which has no expiry of its own and would otherwise outlive the
  // session and mis-scope the next one. Also removes the copilot bubble.
  clearStoredSession()

  if (!authRedirectInProgress) {
    authRedirectInProgress = true
    notification.error({
      message: 'Session expired',
      description: 'Please sign in again.',
    })
    setTimeout(() => {
      authRedirectInProgress = false
    }, 2000)
  }

  if (history.location.pathname !== '/auth/login') {
    history.push('/auth/login')
  }
}

const requestFreshToken = (savedRefreshToken) => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = refreshClient
      .post('auth/refresh-token', {
        refreshToken: savedRefreshToken,
      })
      .finally(() => {
        refreshTokenRequest = null
      })
  }

  return refreshTokenRequest
}

// Request Interceptor
apiClient.interceptors.request.use((request) => {
  const accessToken = Cookies.get('accessToken')
  const userId = getLoggedInUserInfo()?.userId
  const isPublicAuthRequest = PUBLIC_AUTH_ROUTE.test(String(request?.url || ''))

  // Reaching the login screen means the frozen session is fully torn down.
  if (isPublicAuthRequest) {
    endSessionTeardown()
  } else if (isSessionFrozen() || isSessionTeardownInProgress()) {
    // The session is on its way out; an authenticated call now can only fail with a 401
    // the user cannot act on, so drop it before it leaves the browser.
    return Promise.reject(new axios.Cancel('Session ended'))
  }

  if (request?.params) {
    request.params = transformEmpCodePayload(request.params, true)
  }
  if (request?.data instanceof FormData) {
    request.data = transformEmpCodeFormData(request.data, true)
    delete request.headers['Content-Type']
    delete request.headers.common?.['Content-Type']
  } else if (request?.data && isPlainObject(request.data)) {
    request.data = transformEmpCodePayload(request.data, true)
  }

  if (typeof request?.url === 'string') {
    request.url = request.url.replace(/([?&])empCode=([^&]+)/g, '$1emp_code=$2&empId=$2')
  }

  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`
    request.headers.userId = userId
  }

  // Trace client request time for audit activity tracing
  request.headers['X-Client-Request-Time'] = new Date().toISOString() // This is changed

  store.dispatch(toggleLoading({ isLoading: true }))
  NProgress.start()
  return request
})

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    const suppressErrorNotification = Boolean(response?.config?.suppressErrorNotification)
    // Guard 3: Stop completely empty or structural undefined responses before they hit JSON.parse down the line
    if (!response || response.data === undefined) {
      store.dispatch(toggleLoading({ isLoading: false }))
      NProgress.done()
      return { data: { status: false, message: 'Empty or corrupt response payload from server.' } }
    }

    // Guard 4: If the backend returned a literal string "undefined", sanitize it into a standardized payload
    if (response.data === 'undefined') {
      response.data = {
        status: false,
        message: 'Server returned an unparsable undefined tracking state.',
      }
    }

    // The API is authoritative. This catches cached or unexpected employee-list
    // payloads from any module before they can populate a selector or filter.
    // Skip binary responses (e.g. arraybuffer/blob document/image fetches) — walking
    // them via Object.entries strips their bytes and corrupts the payload.
    const responseType = response?.config?.responseType
    if (responseType !== 'arraybuffer' && responseType !== 'blob') {
      response.data = hideExecutiveWorkforceRecords(response.data)
    }

    // Standard business status tracking execution
    if (response?.data?.status === false && !suppressErrorNotification) {
      // This is changed
      notification.error({
        message: 'Error',
        description:
          getErrorDescription(response?.data?.message) || 'An operational error occurred.',
      })
    }

    store.dispatch(toggleLoading({ isLoading: false }))
    NProgress.done()
    return response
  },
  async (error) => {
    // Requests dropped during a session teardown, and replies to calls that were already in
    // flight when it started, are expected noise — fail them silently instead of showing an
    // auth error on top of the "Session Locked" flow.
    if (axios.isCancel(error) || isSessionTeardownInProgress()) {
      stopRequestLoader()
      return Promise.reject(error)
    }

    // Fail-safe error processing execution fallback block
    const statusCode = error?.response?.status ? error.response.status.toString() : ''
    const originalRequest = error?.config
    const suppressErrorNotification = Boolean(originalRequest?.suppressErrorNotification)
    const savedRefreshToken = Cookies.get('refreshToken')
    const requestUrl = String(originalRequest?.url || '')
    const isLoginRequest = LOGIN_ROUTE.test(requestUrl)
    // the original 401/403 may already carry the real reason (e.g. an admin config
    // change) even though the refresh-token endpoint's own error response doesn't
    const originalReasonCode = getSessionInvalidationCode(error)

    // A 403 from the token check carries no application code -- auth-middleware
    // sends a bare INVALID_TOKEN body. A business refusal always names itself
    // (USER_LIMIT_REACHED, OWNERSHIP_CHECK_ERROR, ...), and no token refresh can
    // change that answer: retrying only replays the request, which for a write
    // means running the whole server-side transaction a second time for nothing
    // and reporting the same refusal twice.
    const applicationCode = error?.response?.data?.code
    const isBusinessRefusal =
      statusCode === '403' && applicationCode && !isSessionInvalidationCode(applicationCode)

    if (
      originalRequest &&
      !originalRequest.retryRequest &&
      savedRefreshToken &&
      (statusCode === '401' || statusCode === '403') &&
      !isBusinessRefusal &&
      !requestUrl.includes('auth/refresh-token') &&
      !isLoginRequest
    ) {
      originalRequest.retryRequest = true

      if (originalReasonCode === SESSION_INVALIDATED_CODE) {
        clearSessionAndRedirect(originalReasonCode)
        stopRequestLoader()
        return Promise.reject(error)
      }

      try {
        const refreshResponse = await requestFreshToken(savedRefreshToken)
        const nextAccessToken = refreshResponse?.data?.token
        const nextRefreshToken = refreshResponse?.data?.refreshToken

        if (nextAccessToken) {
          Cookies.set('accessToken', nextAccessToken, { expires: 1 })
          updateCopilotToken(nextAccessToken) // keep the copilot session fresh
          if (nextRefreshToken) {
            Cookies.set('refreshToken', nextRefreshToken, { expires: 7 })
          }
          // Refresh does not re-issue the permission map, so re-stamp the cookie we
          // already hold. Left alone it keeps its original one-day expiry from login and
          // dies while the renewed token lives on - a session that looks signed in but
          // can open nothing, which protectedRoutes used to answer with /auth/404.
          const currentPermissions = Cookies.get('permissions')
          if (currentPermissions !== undefined) {
            Cookies.set('permissions', currentPermissions, { expires: 1 })
          }
          originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`
          store.dispatch(toggleLoading({ isLoading: false }))
          NProgress.done()
          return apiClient(originalRequest)
        }
        clearSessionAndRedirect(
          getSessionInvalidationCode({ response: refreshResponse }) || originalReasonCode,
        )
        stopRequestLoader()
        return Promise.reject(error)
      } catch (refreshError) {
        clearSessionAndRedirect(getSessionInvalidationCode(refreshError) || originalReasonCode)
        stopRequestLoader()
        return Promise.reject(refreshError)
      }
    }

    if (statusCode?.startsWith('5') && !suppressErrorNotification) {
      // This is changed
      notification.error({
        message: 'Error',
        description:
          getErrorDescription(error?.response?.data?.message) ||
          error?.message ||
          'Please contact the administrator',
      })
    } else if (statusCode?.startsWith('4') && !suppressErrorNotification) {
      // This is changed
      notification.error({
        message: 'Error',
        description:
          getErrorDescription(error?.response?.data?.message) ||
          'Unauthorized access or bad payload configuration request.',
      })
    } else if (!suppressErrorNotification) {
      notification.error({
        message: 'Error',
        description: 'Something went wrong',
      })
    }
    // Log response times/metrics for client audit logs tracing on failure
    const reqTime = error?.config?.headers?.['X-Client-Request-Time']
    if (reqTime) {
      const duration = Date.now() - new Date(reqTime).getTime()
      console.error(
        `[Client Audit Trace Error] API: ${
          error?.config?.url
        } | Method: ${error?.config?.method?.toUpperCase()} | Status: ${
          error?.response?.status || 'network_error'
        } | Duration: ${duration}ms`,
      ) // This is changed
    }

    stopRequestLoader()
    return Promise.reject(error)
  },
)

export default apiClient
